import type {
  ABTestSerializer,
  UserSafeModeSerializer,
  UserBonusesInfo,
  UpdateUserSafemodeRequest,
  IApiResponse,
  IResponse,
  AuthByOTPUserInput,
  ShortUserPhoneSerializer,
  OkResponse_UserMonetizationInfoSerializer_ as UserMonetizationDetailsResponse,
  OkResponse_DetailedUserSerializer_ as DetailedUserResponse,
  DetailedUserSerializer,
  ShortUserSerializerWithCustomSocials,
  UpdateUserRequest,
  UpdateUserPrivacyRequest,
} from "@litres-libs/types";

import {
  isOtpInput,
  type EmailInput,
  type OtpInput,
  type SetUserEmailInput,
} from "./foundationUser.types";

import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";
import { createRetryingRequestOptions } from "../api/retrying";

export const foundationUserApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getUserData: build.query<
      ShortUserSerializerWithCustomSocials,
      { SID: string }
    >({
      query: ({ SID }) => ({
        url: "/foundation/api/users/me",
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (
        response: IApiResponse<ShortUserSerializerWithCustomSocials>
      ) => {
        return response.payload.data;
      },
    }),
    /* В модуле авторизации используется этот же эндпоинт, но т.к. нам необходимо сеттить данные в стор
     * механика с релоадом страницы после авторизации ломается, т.к. меняется стейт foundationUser
     * поэтому как временное решение создан еще один эндпоинт, который используется только на SSR
     * чтобы это исправить надо рефакторить код авторизации связанный с перезагрузкой страницы в авторизации
     * или оставить этот эндпоинт */
    getUserDataForSSR: build.query<
      ShortUserSerializerWithCustomSocials,
      string | undefined
    >({
      query: SID => ({
        url: "/foundation/api/users/me",
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (
        response: IApiResponse<ShortUserSerializerWithCustomSocials>
      ) => {
        return response.payload.data;
      },
      extraOptions: {
        ...createRetryingRequestOptions({
          /**
           * Запрос выполняется на стороне SSR почти на каждой странице.
           * Примерно в 1% случаев время ожидания ответа превышает пороговые значения, смотри https://jira.mybook.tech/browse/TECHN-2949.
           * Связано это не с долгой обработкой, по какой-то причине запрос вообще теряется и остаётся без ответа.
           * Поэтому при выполнении этого запроса на стороне SSR пытаемся один раз выполнить retry. Высока вероятность, что при повторном запросе ответ будет получен.
           */
          server: {
            maxRetry: 1,
            delay: 100,
          },
          client: {
            maxRetry: 0,
          },
        }),
      },
    }),
    getMonetizationDetails: build.query<
      UserMonetizationDetailsResponse["payload"]["data"],
      void
    >({
      query: () => ({
        url: "/foundation/api/users/me/monetization-details",
        headers: {
          "accept-version": "2",
        },
      }),
      transformResponse: (response: UserMonetizationDetailsResponse) => {
        return response.payload.data;
      },
      providesTags: [TagsType.USER_MONETIZATION_DATA],
    }),
    getAbTests: build.query<ABTestSerializer[], { SID?: string }>({
      query: ({ SID }) => ({
        url: "/foundation/api/ab-tests",
        method: "POST",
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (response: IApiResponse<ABTestSerializer[]>) =>
        response.payload.data,
    }),
    setUserPhone: build.mutation<boolean, AuthByOTPUserInput>({
      query: payload => {
        return {
          url: "/foundation/api/users/me/set-phone",
          method: "POST",
          body: {
            ...payload,
          },
        };
      },
      transformResponse: (response: IApiResponse<ShortUserPhoneSerializer>) =>
        response.payload.data.is_phone_confirmed,
    }),
    setUserEmail: build.mutation<IResponse, SetUserEmailInput>({
      query: input => {
        let body: EmailInput | OtpInput;

        if (isOtpInput(input)) {
          // Handle the case with otp_id, code, and mail_subscriptions_allowed
          body = {
            otp_id: input.otp_id,
            code: input.code,
            mail_subscriptions_allowed: input.mail_subscriptions_allowed,
          };
        } else {
          // Handle the case with only email
          body = { email: input.email };
        }

        return {
          url: "/foundation/api/users/me/email",
          method: "PUT",
          body,
        };
      },
    }),
    getFoundationUserDetailedInfo: build.query<DetailedUserSerializer, void>({
      query: () => ({
        url: "/foundation/api/users/me/detailed",
        method: "GET",
      }),
      providesTags: [TagsType.USER_DETAILED_DATA],
      transformResponse: (response: DetailedUserResponse) =>
        response.payload.data,
    }),
    getBonuses: build.query<UserBonusesInfo, void>({
      query: () => ({
        url: "/foundation/api/users/me/loyalty/balance/bonuses",
        method: "GET",
      }),
      transformResponse: (response: IApiResponse<UserBonusesInfo>) =>
        response.payload.data,
    }),
    getSafemode: build.query<UserSafeModeSerializer, void>({
      query: () => ({
        url: "/foundation/api/users/me/safemode",
        method: "GET",
      }),
      transformResponse: (response: IApiResponse<UserSafeModeSerializer>) =>
        response.payload.data,
    }),
    setSafemode: build.mutation<IResponse, UpdateUserSafemodeRequest>({
      query: args => ({
        url: "/foundation/api/users/me/safemode",
        method: "PUT",
        body: args,
      }),
    }),
    setPrivacy: build.mutation<IResponse, UpdateUserPrivacyRequest>({
      query: args => ({
        url: "/foundation/api/users/me/privacy",
        method: "PUT",
        body: args,
      }),
    }),
    updateUserInfo: build.mutation<
      DetailedUserResponse,
      Pick<
        UpdateUserRequest,
        "nickname" | "first_name" | "last_name" | "birthday" | "description"
      >
    >({
      query: args => ({
        url: "/foundation/api/users/me",
        method: "PATCH",
        body: args,
      }),
      invalidatesTags: [TagsType.USER_DETAILED_DATA],
    }),
    updateUserAvatar: build.mutation<void, FormData>({
      query: body => ({
        url: "/foundation/api/users/me/avatar",
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
        },
        body,
      }),
    }),
  }),
});
